import type { ToastObject } from '@base-ui/react/toast'
import type { BufferedToastManager } from './manager'
import type {
  SwipeDirection,
  ToastData,
  ToastManagerAddOptions,
  ToastManagerPromiseOptions,
  ToastManagerUpdateOptions,
} from './types'

import { createContext, useContext } from 'react'

import { ToastPosition, ToastVariant } from './types'

/** Default auto-dismiss duration (matches Base UI default). */
export const DEFAULT_TIMEOUT_MS = 5000

const swipeTop: SwipeDirection[] = ['up', 'left', 'right']
const swipeBottom: SwipeDirection[] = ['down', 'left', 'right']

export function getSwipeDirectionForPosition(
  position: ToastPosition,
): SwipeDirection[] {
  switch (position) {
    case ToastPosition.TopLeft:
    case ToastPosition.TopCenter:
    case ToastPosition.TopRight:
      return swipeTop
    default:
      return swipeBottom
  }
}

const toastVariantValues = new Set<string>(Object.values(ToastVariant))

export function resolveVariant(toast: ToastObject<object>): ToastVariant {
  return toastVariantValues.has(toast.type ?? '')
    ? (toast.type as ToastVariant)
    : ToastVariant.Informational
}

/**
 * Resolves a promise option value (string | object | function) so that
 * plain strings become `{ title }` instead of Base UI's default `{ description }`.
 */
function resolvePromiseOption<Value>(
  option:
    | string
    | ToastManagerUpdateOptions<ToastData>
    | ((result: Value) => string | ToastManagerUpdateOptions<ToastData>),
  result: Value,
): ToastManagerUpdateOptions<ToastData> {
  if (typeof option === 'string') {
    return { title: option }
  }
  if (typeof option === 'function') {
    const resolved = option(result)
    return typeof resolved === 'string' ? { title: resolved } : resolved
  }
  return option
}

/**
 * Typed toast API over the provider's manager.
 *
 * - `add()` enforces that `description` requires `title`.
 * - `promise()` maps plain-string options to `title` instead of Base UI's
 *   default `description`, and enforces the same constraint for object options.
 */
export function createToastApi(manager: BufferedToastManager) {
  return {
    add(options: ToastManagerAddOptions) {
      const { autoDismiss, ...rest } = options
      const baseOptions = autoDismiss === false ? { ...rest, timeout: 0 } : rest
      return manager.add(baseOptions)
    },
    update: manager.update,
    close: manager.close,
    promise<Value>(
      promiseValue: Promise<Value>,
      options: ToastManagerPromiseOptions<Value, ToastData>,
    ) {
      const loadingResolved =
        typeof options.loading === 'string'
          ? { title: options.loading }
          : options.loading
      // type: 'loading' is not a recognized ToastVariant, so resolveVariant
      // falls back to ToastVariant.Informational intentionally.
      const id = manager.add({ ...loadingResolved, type: 'loading' })

      return promiseValue.then(
        (result) => {
          manager.update(id, {
            ...resolvePromiseOption(options.success, result),
            type: 'success',
          })
          return result
        },
        (error: unknown) => {
          manager.update(id, {
            ...resolvePromiseOption(options.error, error),
            type: 'error',
          })
          throw error
        },
      )
    },
  }
}

export const ToastApiContext = createContext<ReturnType<
  typeof createToastApi
> | null>(null)
ToastApiContext.displayName = 'ToastApiContext'

/** Stable per `<Toast>` region, and does not re-render on toast changes. */
export function useToastManager() {
  const toastApi = useContext(ToastApiContext)
  if (!toastApi) {
    throw new Error('Toast.useToastManager must be used within <Toast>.')
  }
  return toastApi
}
